<template>
    
    <button
        class="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 bg-white transition-colors duration-300 hover:bg-blue-500 hover:text-white rounded-md text-sm"
        @click="redirectToLastPage()"
    >
        <svg class="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Retour 
    </button>

    <FormKit
        type="form"
        :form-class="isFormSubmitted ? 'hide' : 'show'"
        @submit="submitFormHandler"
        :actions="false"
    >
        <div class="card">
            <DataTable
                ref="dt"
                lazy
                dataKey="uuid"
                stateStorage="local"
                :state-key="tableLocalStorageKey"
                :value="tableData"
                :paginator="true"
                :rows="rows"
                :rowHover="true"
                :loading="isLoading"
                :totalRecords="totalRecords"
                :currentPage="currentPage"
                :rowsPerPageOptions="rowsPerPageOptions"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} produits"
                :selectAll="isSelectAllBtnChecked"
                v-model:selection="selectedProducts"
                @select-all-change="onSelectAllChange($event)"
                @row-select="onRowSelect($event)"
                @row-unselect="onRowUnselect($event)"
                @page="onPaginate($event)">
                <template #header>
                    <div class="flex flex-row flex-nowrap card-header border-0">
                        <div class="flex card-toolbar w-full">
                            <div class="w-full">
                            <span class="p-input-icon-left w-10/12">
                                <i class="pi pi-search" />
                                <InputText type="text" class="w-9/12" v-model="criteria" placeholder="Rechercher un produit" @input="onSearchInputDt()" />
                            </span>
                            </div>
                        </div>

                        <div class="card-toolbar w-full">
                            <div class="flex flex-col justify-center">
                                <p>Nombre total de produits : {{ totalRecords }}</p>
                                <p>Nombre total de produits déjà sélectionné : {{ totalSelectedVariantProductsInDatabase }}</p>
                                <p>Nombre de produit à rajouter : {{ totalSelectedProductsToAdd }}</p>
                                <p>Nombre de produit à supprimer : {{ totalUnSelectedProducts }}</p>
                                <p v-if="isInFilteredView">Vous êtes en vue filtrée</p>
                            </div>
                        </div>

                        <div class="card-toolbar w-full">
                            <FormKit
                                type="taglist"
                                name="selected-category-filter"
                                label="Rechercher une catégorie"
                                placeholder="Example: E-billet"
                                v-model="selectedOptionsCategory"
                                :options="searchCategories"
                                selection-appearance="option"
                                multiple
                                empty-message="Aucune catégorie trouvée"
                                load-on-created="false"
                                loadOnScroll="false"
                                debounce="1000"
                            >
                            </FormKit>
                        </div>
                    </div>
                </template>
                <div class="card-body pt-0">
                    <Column selectionMode="multiple"></Column>
                    <Column field="name" header="Produit">
                        <template #body="slotProps">
                            <span>{{ slotProps.data.name }}</span>
                        </template>
                    </Column>
                    <Column field="brand" header="Marque">
                        <template #body="slotProps">
                            <span>{{ slotProps.data.brand }}</span>
                        </template>
                    </Column>
                    <Column field="category" header="Catégorie">
                        <template #body="slotProps">
                            <span v-if="slotProps.data.categories?.length > 0" :title="formatCategoriesProduct(slotProps.data.categories)">
                                {{ slotProps.data.categories[0].value }}
                            </span>
                        </template>
                    </Column>
                </div>
                <template #empty>Aucun produit trouvé.</template>
            </DataTable>
        </div>
        <div class="save-form-container">
            <FormKit type="submit" label="Enregistrer" :disabled="isLoading" />
        </div>
    </FormKit>
</template>

<script setup lang="ts">
import { fetchVariantProductsByPropertyValue, formatCategoriesProduct } from "@/_services/product/variant/productVariant";
import { addVariantProductsToPropertyValue } from "@/_services/propertyValue/propertyValue";
import { computed, onBeforeMount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { IProductVariantForProperty } from "@/models/product/variant/IProductVariantForProperty";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import { fetchCategoriesForDropDown } from "@/_services/category/category";
import { IAddVariantProductsToPropertyValue } from "@/models/product/property/PropertyValue";
import router from "@/router";
import swal from "@/_helpers/swal";

const productVariants = Array<IProductVariantForProperty>();
const rows = ref<number>(10);
const page = ref < number > (1);
const criteria = ref<string>("");
const currentPage = ref<number>(0);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100, 250];
const totalRecords = ref<number>(0);
const isLoading = ref<boolean>(true);
const tableData = ref<Array<IProductVariantForProperty>>(productVariants);
const route = useRoute();
const propertyValueUuidRouteParam = ref<string | null>(null);
const propertyUuidRouteParam = ref<string>('');
const selectedOptionsCategory = ref<string[]>([]);
const isFormSubmitted = ref<boolean>(false);
const isSelectAllBtnChecked = ref<boolean>(false);
const tableLocalStorageKey = "selectedTableVariantProduct";
const selectedProductToAddStorageKey = "selectedVariantProductsToAdd";
const selectedProductToRemoveStorageKey = "selectedVariantProductsToRemove";
const selectAllBtnStatusStorageKey = "selectAllVariantProductsBtnStatus";
const dbProductsOfPropertyValueInStorage = "dbProductsOfPropertyValueInStorage";
const totalSelectedProductsToAdd = ref<number>(0);
const totalUnSelectedProducts = ref<number>(0);
const refItemsPerPage = ref<number>(0);
const selectedProducts = ref(Array<IProductVariantForProperty>());
const productsOfCurrentPage = ref(Array<IProductVariantForProperty>());
const totalSelectedVariantProductsInDatabase = ref<number>(0);

onBeforeMount(() => {
    initLocalStorageData();
});

onMounted(async () => {
    propertyUuidRouteParam.value = route.params.propertyUuid as string;
    propertyValueUuidRouteParam.value = route.params.uuid as string;
    if (propertyValueUuidRouteParam.value) {
        await populateDataTableWithLocalStorageFilters();
    }
});

watch(selectedOptionsCategory, () => {
    onChangeSelectedCategory();
});

watch(isSelectAllBtnChecked, () => {
    setIsSelectedAllBtnStatusLocalStorage(isSelectAllBtnChecked.value);
});

const isInFilteredView = computed(() => {
    return selectedOptionsCategory.value.length > 0
        || totalSelectedProductsToAdd.value > 0
        || totalUnSelectedProducts.value > 0;
})

async function searchCategories({ search, page, hasNextPage }) {
    const [totalCategoriesRecordsForPickListApi, categoriesForPickListApi] = await fetchCategoriesForDropDown(5, page, search);
    const hasItems = categoriesForPickListApi.length > 0;
    if (hasItems && page !== totalCategoriesRecordsForPickListApi) {
        hasNextPage();
    }
    
    return categoriesForPickListApi;
}

const populateDataTableWithLocalStorageFilters = async (itemsPerPage?: number, page?: number) => {
    const localStorageRowPerPage = getLocalStorageRowsPerPage();
    const localStorageFisrtPageState = getLocalStorageFisrtPageState();
    let currentPage = page;
    if (!currentPage) {
        currentPage = localStorageFisrtPageState / localStorageRowPerPage + 1;
    }
    let currentItemsPerPage = itemsPerPage;
    if (!currentItemsPerPage) {
        currentItemsPerPage = localStorageRowPerPage;
    }

    await populateDatatable(currentItemsPerPage as number, currentPage, criteria.value, selectedOptionsCategory.value);
}

const populateDatatable = async (itemsPerPage: number, page: number, criteria: string, categoriesFilter?: string[]) => {
    const [loadingDt, totalRecordsDt, tableDataDt] = await fetchVariantProductsByPropertyValue(itemsPerPage, page, criteria, false, propertyValueUuidRouteParam.value, categoriesFilter);
    totalRecords.value = totalRecordsDt;
    isLoading.value = loadingDt;
    tableData.value = tableDataDt;
    refItemsPerPage.value = itemsPerPage;
    productsOfCurrentPage.value = tableData.value;
    totalSelectedVariantProductsInDatabase.value = productsOfCurrentPage.value[0]?.totalSelectedVariantProducts;
    const selectedProductsInDatabaseOnCurrentPage = productsOfCurrentPage.value.filter(item => item.isLinkedPropertyValueParam === true);
    const selectedProductsUuidsInDatabaseOnCurrentPage = selectedProductsInDatabaseOnCurrentPage.map(item => item.uuid);
    addDbProductsToPropertyValueProductsInStorage(selectedProductsUuidsInDatabaseOnCurrentPage);
    let selectedProductsOnCurrentPage: IProductVariantForProperty[] = [];
    selectedProductsOnCurrentPage = productsOfCurrentPage.value.filter((item: IProductVariantForProperty) =>
        (getSelectedProductsToAddFromStorage().includes(item.uuid) || getDatabaseSelectedProductOfPropertyValueFromStorage().includes(item.uuid))
        && !getUnSelectedProductsToRemoveFromStorage().includes(item.uuid));
    
    const totalUnSelectedProductsOnCurrentPage = getTotalUnSelectedProductsOnCurrentPage();
    isSelectAllBtnChecked.value = (itemsPerPage - totalUnSelectedProductsOnCurrentPage) === itemsPerPage;
    selectedProducts.value = selectedProductsOnCurrentPage;
};

const getDatabaseSelectedProductOfPropertyValueFromStorage = (): string[] => {
    const initSelectedProductsOfPropertyValue = localStorage.getItem(dbProductsOfPropertyValueInStorage);
    return initSelectedProductsOfPropertyValue ? JSON.parse(initSelectedProductsOfPropertyValue) : [];
}

const addDbProductsToPropertyValueProductsInStorage = (UuidsCheckedInDb: string[]) => {
    let initProductsOfPropertyValueInStorage = getDatabaseSelectedProductOfPropertyValueFromStorage();
    UuidsCheckedInDb.forEach(uuid => {
        if (!initProductsOfPropertyValueInStorage.includes(uuid)) {
            initProductsOfPropertyValueInStorage.push(uuid);
        }
    });
    localStorage.setItem(dbProductsOfPropertyValueInStorage, JSON.stringify(initProductsOfPropertyValueInStorage));
}

const getSelectedProductsToAddFromStorage = (): string[] => {
    const selectedProductsToAdd = localStorage.getItem(selectedProductToAddStorageKey);
    return selectedProductsToAdd ? JSON.parse(selectedProductsToAdd) : [];
}

const addSelectedProductInProductsToAddInStorage = (uuids: string[]) => {
    if (uuids.length === 0) {
        totalSelectedProductsToAdd.value = 0;
        localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify([]));
        return;
    }
    
    let productsToAddInStorage = getSelectedProductsToAddFromStorage();
    uuids.forEach(uuid => {
        if (!productsToAddInStorage.includes(uuid)) {
            productsToAddInStorage.push(uuid);
        }
    });
    localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify(productsToAddInStorage));
}

const removeUnSelectedProductFromProductsToAddInStorage = (uuids: string[]) => {
    let productsToAddInStorage = getSelectedProductsToAddFromStorage();
    let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !uuids.includes(uuid));
    localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify(newProductsToAddInStorage));
}

const getUnSelectedProductsToRemoveFromStorage = (): string[] => {
    const unSelectedProductsToRemove = localStorage.getItem(selectedProductToRemoveStorageKey);
    return unSelectedProductsToRemove ? JSON.parse(unSelectedProductsToRemove) : [];
}

const addUnSelectedProductInProductsToRemoveInStorage = (uuids: string[]) => {
    if (uuids.length === 0) {
        totalUnSelectedProducts.value = 0;
        localStorage.setItem(selectedProductToRemoveStorageKey, JSON.stringify([]));
        return;
    }
    
    let productsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
    uuids.forEach(uuid => {
        if (!productsToRemoveInStorage.includes(uuid)) {
            productsToRemoveInStorage.push(uuid);
        }
    });
    localStorage.setItem(selectedProductToRemoveStorageKey, JSON.stringify(productsToRemoveInStorage));
}

const removeSelectedProductInProductsToRemoveInStorage = (uuidsToRemove: string[]) => {
    let currentProductsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
    let newProductsToRemoveInStorage = currentProductsToRemoveInStorage.filter(uuid => !uuidsToRemove.includes(uuid));
    localStorage.setItem(selectedProductToRemoveStorageKey, JSON.stringify(newProductsToRemoveInStorage));
}

const onChangeSelectedCategory = async (): Promise<void> => {
    showSpinner();
    const search = criteria.value.trim();
    await populateDatatable(rows.value, page.value, "" != search ? search : "", selectedOptionsCategory.value);
};

const onSelectAllChange = async (event): Promise<void> => {
    if (true === event.checked) {
        isSelectAllBtnChecked.value = true;
        selectedProducts.value = productsOfCurrentPage.value;
        const selectedProductsUuidsToRemoveInStorage = productsOfCurrentPage.value
            .filter((item: IProductVariantForProperty) =>
                getDatabaseSelectedProductOfPropertyValueFromStorage().includes(item.uuid) ||
                getUnSelectedProductsToRemoveFromStorage().includes(item.uuid)
            )
            .map(item => item.uuid);
        removeSelectedProductInProductsToRemoveInStorage(selectedProductsUuidsToRemoveInStorage);

        const selectedProductUuidToSaveInDatabase = productsOfCurrentPage.value
            .filter((item: IProductVariantForProperty) =>
                !getDatabaseSelectedProductOfPropertyValueFromStorage().includes(item.uuid)
            )
            .map(item => item.uuid);
        if (selectedProductUuidToSaveInDatabase.length > 0) {
            addSelectedProductInProductsToAddInStorage(selectedProductUuidToSaveInDatabase);
            const currentProductsToAddInStorage = getSelectedProductsToAddFromStorage();
            let newProductsToAddInStorage = currentProductsToAddInStorage;
            selectedProductUuidToSaveInDatabase.forEach((uuid: string) => {
                if (!currentProductsToAddInStorage.includes(uuid)) {
                    newProductsToAddInStorage.push(uuid);
                }
            });
            totalSelectedProductsToAdd.value = newProductsToAddInStorage.length;
        }

        const currentProductsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
        let newTotalProductsToRemoveInStorage = currentProductsToRemoveInStorage.filter(uuid => !selectedProductsUuidsToRemoveInStorage.includes(uuid));
        totalUnSelectedProducts.value = newTotalProductsToRemoveInStorage.length;
    } else {
        isSelectAllBtnChecked.value = false;
        const unSelectedProductsToRemoveInDatabase = productsOfCurrentPage.value
            .filter((item: IProductVariantForProperty) =>
                !getDatabaseSelectedProductOfPropertyValueFromStorage().includes(item.uuid) ||
                getSelectedProductsToAddFromStorage().includes(item.uuid)
            );
        const unSelectedProductsUuidsToRemoveInDatabase = unSelectedProductsToRemoveInDatabase.map(item => item.uuid);
        removeUnSelectedProductFromProductsToAddInStorage(unSelectedProductsUuidsToRemoveInDatabase);

        const unSelectedProductsToRemoveInStorage = productsOfCurrentPage.value
            .filter((item: IProductVariantForProperty) =>
                getDatabaseSelectedProductOfPropertyValueFromStorage().includes(item.uuid)
            );
        const unSelectedProductsUuidsToRemoveInStorage = unSelectedProductsToRemoveInStorage.map(item => item.uuid);
        if (unSelectedProductsUuidsToRemoveInStorage.length > 0) {
            addUnSelectedProductInProductsToRemoveInStorage(unSelectedProductsUuidsToRemoveInStorage);
            const productsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
            let newProductsToRemoveInStorage = productsToRemoveInStorage;
            unSelectedProductsUuidsToRemoveInStorage.forEach((uuid: string) => {
                if (!productsToRemoveInStorage.includes(uuid)) {
                    newProductsToRemoveInStorage.push(uuid);
                }
            });
            totalUnSelectedProducts.value = newProductsToRemoveInStorage.length;
        }

        const productsToAddInStorage = getSelectedProductsToAddFromStorage();
        let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !unSelectedProductsUuidsToRemoveInDatabase.includes(uuid));
        totalSelectedProductsToAdd.value = newProductsToAddInStorage.length;
        selectedProducts.value = selectedProducts.value.filter((item: IProductVariantForProperty) =>
            !productsOfCurrentPage.value.map((product: IProductVariantForProperty) => product.uuid).includes(item.uuid)
        );

        hideSpinner();
    }
};

const onRowSelect = async (event: any): Promise<void> => {
    const selectedProductUuid = event.data.uuid;
    removeSelectedProductInProductsToRemoveInStorage([selectedProductUuid]);
    if (!getDatabaseSelectedProductOfPropertyValueFromStorage().includes(selectedProductUuid)) {
        totalSelectedProductsToAdd.value++;
        addSelectedProductInProductsToAddInStorage([selectedProductUuid]);
    }

    const currentProductsToRemoveInStorage = getUnSelectedProductsToRemoveFromStorage();
    let newTotalProductsToRemoveInStorage = currentProductsToRemoveInStorage.filter(uuid => !selectedProductUuid.includes(uuid));
    totalUnSelectedProducts.value = newTotalProductsToRemoveInStorage.length;
    const totalSelectedProductsOnCurrentPage = getTotalUnSelectedProductsOnCurrentPage();
    isSelectAllBtnChecked.value = (refItemsPerPage.value - totalSelectedProductsOnCurrentPage) === refItemsPerPage.value;
}

const onRowUnselect = async (event: any): Promise<void> => {
    const unSelectedProductUuid = event.data.uuid;
    removeUnSelectedProductFromProductsToAddInStorage([unSelectedProductUuid]);
    if (getDatabaseSelectedProductOfPropertyValueFromStorage().includes(unSelectedProductUuid)) {
        addUnSelectedProductInProductsToRemoveInStorage([unSelectedProductUuid]);
        totalUnSelectedProducts.value = getUnSelectedProductsToRemoveFromStorage().length++;
    }

    const productsToAddInStorage = getSelectedProductsToAddFromStorage();
    let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !unSelectedProductUuid.includes(uuid));
    totalSelectedProductsToAdd.value = newProductsToAddInStorage.length;
    isSelectAllBtnChecked.value = false;
}

const getTotalUnSelectedProductsOnCurrentPage = (): number => {
    const totalUnselectedCurrentPageProductsNotInDbAndAddFromStorage = productsOfCurrentPage.value.filter((item: IProductVariantForProperty) =>
        (!getSelectedProductsToAddFromStorage().includes(item.uuid) && !getDatabaseSelectedProductOfPropertyValueFromStorage().includes(item.uuid))).length;
    const totalUnselectedCurrentPageProductsOnStorage = productsOfCurrentPage.value.filter((item: IProductVariantForProperty) =>
        getUnSelectedProductsToRemoveFromStorage().includes(item.uuid))
        .length;

    return totalUnselectedCurrentPageProductsOnStorage + totalUnselectedCurrentPageProductsNotInDbAndAddFromStorage;
};

const getLocalStorageData = () => {
    let storageData = localStorage.getItem(tableLocalStorageKey);
    if (storageData) {
        return JSON.parse(storageData);
    }

    return null;
}

const setLocalStorageRowsPerPage = (rowPerPage: number) =>{
    let storageData = localStorage.getItem(tableLocalStorageKey);
    if (storageData) {
        let localStorageObject = JSON.parse(storageData);
        localStorageObject.rows = rowPerPage;
        localStorage.setItem(tableLocalStorageKey, JSON.stringify(localStorageObject));
    }
}

const setIsSelectedAllBtnStatusLocalStorage = (status: boolean) =>{
    isSelectAllBtnChecked.value = status;
    
    const isSelectedAllBtnInStorageData = localStorage.getItem(selectAllBtnStatusStorageKey);
    if (!isSelectedAllBtnInStorageData) {
        localStorage.setItem(selectAllBtnStatusStorageKey, status.toString());
    } else {
        const currentStatus = JSON.parse(isSelectedAllBtnInStorageData);
        if (currentStatus !== status) {
            localStorage.setItem(selectAllBtnStatusStorageKey, status.toString());
        }
    }
}

const getIsSelectedAllBtnStatusLocalStorage = (): boolean => {
    const isSelectedAllBtnInStorageData = localStorage.getItem(selectAllBtnStatusStorageKey);
    if (isSelectedAllBtnInStorageData) {
        return isSelectedAllBtnInStorageData === 'true';
    }

    return false;
}

const getLocalStorageRowsPerPage = () => {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.rows) {
        return localStorageData.rows;
    }

    return defaultRowsPerPage;
}

const getLocalStorageFisrtPageState = () => {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.first) {
        return localStorageData.first;
    }

    return 0;
}

const onPaginate = async (e) => {
    showSpinner();
    rows.value = e.rows;
    currentPage.value = e.page;
    page.value = e.page + 1;

    setLocalStorageRowsPerPage(rows.value);
    await populateDataTableWithLocalStorageFilters(rows.value, page.value);
};

const onSearchInputDt = async () => {
    const search = criteria.value.trim();
    if (search.length === 0 || search.length >= 3) {
        showSpinner();
        await populateDatatable(rows.value, page.value, "" != search ? search : "", selectedOptionsCategory.value);
    }
}

const canSubmitForm = (): boolean => {
    return !(totalSelectedProductsToAdd.value === 0 && totalUnSelectedProducts.value === 0);
}

const submitFormHandler = async (data) => {
    if (!canSubmitForm()) {
        swal.warning("Vous devez selectionner ou désélectionner au moins un produit.");
    } else {
        swal.confirm("Êtes-vous sur de vouloir ajouter les produits ?")
            .then((result) => {
                if (result.isConfirmed) {
                    showSpinner();
                    const propertyValueUuid = propertyValueUuidRouteParam.value as string;
                    const selectedProductsToAdd = getSelectedProductsToAddFromStorage();
                    const unSelectedProductsToRemove = getUnSelectedProductsToRemoveFromStorage();
                    const params: IAddVariantProductsToPropertyValue  = {
                        uuid: propertyValueUuid,
                        isAllSelected: getIsSelectedAllBtnStatusLocalStorage(),
                        selectedProductsUuidsToAdd: selectedProductsToAdd,
                        unSelectedProductsUuidsToRemove: unSelectedProductsToRemove
                    };
                    addVariantProductsToPropertyValue(params).then(() => {
                        swal.success("Créé !", "Vous venez d'ajouter des produits à la valeur de proprité").then(() => {
                            hideSpinner();
                            router.push({ name: 'CustomPropertyValuesList', params: { uuid: propertyUuidRouteParam.value }})
                        });
                    });
                }
            });

        isFormSubmitted.value = true;    
    }
}

const redirectToLastPage = () => {
    router.back();
};

const showSpinner = () => {
    isLoading.value = true;
}
const hideSpinner = () => {
    isLoading.value = false;
}

const initLocalStorageData = () => {
    // Delete
    if (localStorage.getItem(selectAllBtnStatusStorageKey)) {
        localStorage.removeItem(selectAllBtnStatusStorageKey);
    }
    if (localStorage.getItem(tableLocalStorageKey)) {
        localStorage.removeItem(tableLocalStorageKey);
    }
    if (localStorage.getItem(selectedProductToAddStorageKey)) {
        localStorage.removeItem(selectedProductToAddStorageKey);
    }
    if (localStorage.getItem(selectedProductToRemoveStorageKey)) {
        localStorage.removeItem(selectedProductToRemoveStorageKey);
    }
    if (localStorage.getItem(dbProductsOfPropertyValueInStorage)) {
        localStorage.removeItem(dbProductsOfPropertyValueInStorage);
    }

    // Create
    if (!localStorage.getItem(selectAllBtnStatusStorageKey)) {
        localStorage.setItem(selectAllBtnStatusStorageKey, 'false');
    }
    if (!localStorage.getItem(selectedProductToAddStorageKey)) {
        localStorage.setItem(selectedProductToAddStorageKey, '[]');
    }
    if (!localStorage.getItem(selectedProductToRemoveStorageKey)) {
        localStorage.setItem(selectedProductToRemoveStorageKey, '[]');
    }
    if (!localStorage.getItem(dbProductsOfPropertyValueInStorage)) {
        localStorage.setItem(dbProductsOfPropertyValueInStorage, '[]');
    }
}
</script>

<style scoped lang="scss">


.returnLink {
    @apply hover:bg-[#0275ff] hover:text-white bg-white text-[#0275ff]
    transition-all duration-300 p-5 border-[1px] border-[#0275ff] rounded-md;
}
select {
  @apply font-normal;
}
.card-body {
    @apply bg-white;
}
.save-form-container {
    display: flex;
    justify-content: center;
    margin-top: 2.5rem;
}
::v-deep(.p-datatable .p-datatable-tbody > tr.p-datatable-emptymessage > td ) {
    text-align: center;
}
::v-deep(.p-datatable .p-datatable-tbody > tr.p-datatable-emptymessage > td ) {
    text-align: center;
}
::v-deep(.hover\:bg-gray-300:hover) {
    cursor: pointer;
}
</style>